import { useNavigate, useSearch } from "@tanstack/react-router";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth";

import { useApprovals, useMyPayees } from "../api";
import { AdminView } from "../components/AdminView";
import { CollectionsView } from "../components/CollectionsView";
import { ContractsView } from "../components/ContractsView";
import { HowToView } from "../components/HowToView";
import { LedgerView } from "../components/LedgerView";
import { PaidView } from "../components/PaidView";
import { ReportView } from "../components/ReportView";

export type CommissionsTab =
  | "report"
  | "contracts"
  | "ledger"
  | "collections"
  | "paid"
  | "admin"
  | "how-to";

export function CommissionsPage() {
  const isAdmin = useCan("commissions.admin");
  const canRecordPaid = useCan("commissions.write") || isAdmin;
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: CommissionsTab };
  const tab: CommissionsTab = search.tab ?? "report";
  const { data: myPayees } = useMyPayees();
  const approvals = useApprovals(isAdmin);
  const pendingCount = approvals.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Commissions</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          The accounting ledger for sales commissions — cash-on-cash payouts per
          contract, locked monthly at reconciliation step R-12.
          {!isAdmin && (myPayees?.length ?? 0) > 0 && (
            <> Showing your commissions only.</>
          )}
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(t) =>
          navigate({
            to: "/commissions",
            search: { tab: t as CommissionsTab },
            replace: true,
          })
        }
      >
        <TabsList variant="underline">
          <TabsTrigger value="report">Monthly Report</TabsTrigger>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
          <TabsTrigger value="ledger">Contract Ledger</TabsTrigger>
          <TabsTrigger value="collections">Collections</TabsTrigger>
          {canRecordPaid && <TabsTrigger value="paid">Paid Amounts</TabsTrigger>}
          {isAdmin && (
            <TabsTrigger value="admin" count={pendingCount > 0 ? pendingCount : undefined}>
              Admin
            </TabsTrigger>
          )}
          <TabsTrigger value="how-to">How to use</TabsTrigger>
        </TabsList>
        <TabsContent value="report" className="mt-4">
          <ReportView />
        </TabsContent>
        <TabsContent value="contracts" className="mt-4">
          <ContractsView />
        </TabsContent>
        <TabsContent value="ledger" className="mt-4">
          <LedgerView />
        </TabsContent>
        <TabsContent value="collections" className="mt-4">
          <CollectionsView />
        </TabsContent>
        {canRecordPaid && (
          <TabsContent value="paid" className="mt-4">
            <PaidView />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="admin" className="mt-4">
            <AdminView />
          </TabsContent>
        )}
        <TabsContent value="how-to" className="mt-4">
          <HowToView />
        </TabsContent>
      </Tabs>
    </div>
  );
}
